import { useApiClientBase } from "./apiClient";

/**
 * useCommentsApi is a custom hook that provides API functions
 * for saving, drafting, submitting, and signing off comments.
 * It uses the configured API client and error handler from useApiClientBase.
 *
 * @returns { savePaginatedComments, saveDraft, submitForReview, submitSignOff } - Functions to manage comments lifecycle.
 */
export const useCommentsApi = () => {
  const { apiClient, handleError } = useApiClientBase();

  /**
   * Saves paginated comments to the backend API.
   *
   *  data - The request payload containing comments data to be saved.
   * @returns {Promise<any>} - The response data from the API, or null if an error occurs.
   */
  const savePaginatedComments = async (data: any) => {
    try {
      const response = await apiClient.post(
        "/CascadingFilter/savecomments",
        data
      );
      return response.data;
    } catch (err) {
      return handleError(err, "Saving comments failed");
    }
  };

  /**
   * Saves comments as a draft in the backend API.
   *
   * data - The request payload containing comments data to be saved as draft.
   * @returns {Promise<any>} - The response data from the API, or null if an error occurs.
   */
  const saveDraft = async (data: any) => {
    try {
      const response = await apiClient.post(
        "/CascadingFilter/saveasdraft",
        data
      );
      return response.data;
    } catch (err) {
      return handleError(err, "Saving comments as draft failed");
    }
  };

  /**
   * Submits comments for review to the backend API.
   *
   * data - The request payload containing comments data to be submitted for review.
   * @returns {Promise<any>} - The response data from the API, or null if an error occurs.
   */
  const submitForReview = async (data: any) => {
    try {
      const response = await apiClient.post(
        "/CascadingFilter/submittedforreview",
        data
      );
      return response.data;
    } catch (err) {
      return handleError(err, "Submitting for review failed");
    }
  };

  /**
   * Signs off comments in the backend API.
   *
   * data - The request payload containing user email and comments data for sign off.
   * @returns {Promise<any>} - The response data from the API, or null if an error occurs.
   */
  const submitSignOff = async (data: any) => {
    try {
      const response = await apiClient.post(
        `/CascadingFilter/signoffcomments?UserEmail=${data.userEmail}`,
        data
      );
      return response.data;
    } catch (err) {
      return handleError(err, "Could not Sign off comments");
    }
  };

  return { savePaginatedComments, saveDraft, submitForReview, submitSignOff };
};
